<script lang="ts" setup>
  import { CATEGORIES, DESTIN } from '@/utils/constants/origDest'
  import type { ORIGIN } from '@/utils/constants/origDest'
  import type {
    IOrigin,
    TDestin,
    TFilteredOrigDestResult,
    ICategories,
    TSubCategories,
    TCategories
  } from '@/types/OrigDest'
  import { matchStringCase, removeHtmlTags } from '@/utils/stringManipulation'
  import { cva } from 'class-variance-authority'
  import type { TIcons } from '@/components/icon/types'

  const props = withDefaults(
    defineProps<{
      data: TFilteredOrigDestResult[]
      title: string
      label: string
      categories?: ICategories[] | null
      subCategories?: TSubCategories[] | null
      disabled?: boolean
      icon?: TIcons
      type: typeof ORIGIN | typeof DESTIN
      variant?: 'default' | 'block'
      border?: 'rounded' | 'rounded-left' | 'rounded-right' | 'rounded-left-no-border'
      selectedOrigin?: IOrigin | null
      showAnyDestinOption?: boolean
      openOverTrigger?: boolean
      dialogContentClass?: string
      sizeVariant?: 'default' | 'compact'
      triggerAriaLabel?: string
    }>(),
    {
      categories: null,
      subCategories: null,
      disabled: false,
      icon: undefined,
      variant: 'default',
      border: 'rounded',
      selectedOrigin: null,
      showAnyDestinOption: true,
      openOverTrigger: false,
      dialogContentClass: '',
      sizeVariant: 'default',
      triggerAriaLabel: undefined
    }
  )

  const globalSettingsStore = useGlobalSettingsStore()
  const { anyDestinationLink } = storeToRefs(globalSettingsStore)

  const modelValue = defineModel<IOrigin | TDestin | null>({ default: null })
  const categoryModel = defineModel<TCategories>('category', { default: CATEGORIES.ALL })
  const subCategoryModel = defineModel<string>('subCategory', { default: '' })
  const searchTerm = defineModel<string>('searchTerm', {
    default: ''
  })
  const emit = defineEmits<{
    selected: []
  }>()

  const wideModalCategories: TCategories[] = [CATEGORIES.INTERESTS, CATEGORIES.COUNTRIES]

  const { isDesktop } = useBTBreakpoints()

  const isWideSelect = computed(() => {
    if (!isDesktop.value || searchTerm.value) return false

    return wideModalCategories.includes(categoryModel.value)
  })

  const searchInput = ref<HTMLElement | null>(null)

  const isOpen = defineModel<boolean>('isOpen', { default: false })

  const isKeyboardNavigated = ref(false)
  const isSubCategoriesDisabled = ref(false)

  const openModal = (keyboardNavigation = false) => {
    if (props.disabled) {
      return
    }

    isKeyboardNavigated.value = keyboardNavigation
    isSubCategoriesDisabled.value = keyboardNavigation && isDesktop.value
    isOpen.value = true
  }

  const isDefaultVariant = computed(() => props.variant === 'default')

  const dialogContentClass = computed(() => {
    if (!isDefaultVariant.value) {
      return props.dialogContentClass || ''
    }

    if (props.dialogContentClass) {
      return isWideSelect.value ? `md:w-160 ${props.dialogContentClass}` : `md:w-130 ${props.dialogContentClass}`
    }

    return isWideSelect.value ? 'md:w-160 lg:w-166' : 'md:w-130'
  })

  const autoCompleteSuggestion = ref<string | null>(null)
  const autoSuggestedItem = computed(() => (props.data[1] as IOrigin | TDestin) || null)

  const clearSearchResultTags = (item: IOrigin | TDestin) => {
    item.apt = removeHtmlTags(item.apt)
    item.city = removeHtmlTags(item.city)
    item.code = removeHtmlTags(item.code)
    item.country = removeHtmlTags(item.country)

    return item
  }

  const onSuggestionSelect = () => {
    if (!autoCompleteSuggestion.value) {
      return
    }

    const item = clearSearchResultTags(autoSuggestedItem.value)

    modelValue.value = item
    emit('selected')
    isOpen.value = false
    searchTerm.value = ''
  }

  watchDebounced(
    searchTerm,
    () => {
      if (Number(searchTerm.value.length) < 2 || !autoSuggestedItem.value?.city || !autoSuggestedItem.value?.code) {
        autoCompleteSuggestion.value = null

        return
      }

      const suggestion = `${removeHtmlTags(autoSuggestedItem.value?.city)} (${removeHtmlTags(autoSuggestedItem.value?.code)})`
      const input = searchTerm.value
      autoCompleteSuggestion.value = matchStringCase(suggestion, input)
    },
    { debounce: 100 }
  )

  const origDestSelect = ref<HTMLElement | null>(null)

  watch(isOpen, (newValue) => {
    if (isDesktop.value && newValue) {
      nextTick(() => {
        scrollIntoView(origDestSelect)
      })
    }
  })

  const blockBorderBase = computed(() =>
    cva('border border-border', {
      variants: {
        style: {
          rounded: 'rounded',
          'rounded-left': 'rounded-bl-sm rounded-tl-sm',
          'rounded-right': 'rounded-br-sm rounded-tr-sm',
          'rounded-left-no-border': 'rounded-bl-sm rounded-tl-sm border-r-0'
        }
      }
    })({
      style: props.border
    })
  )

  const searchInputBase = computed(() =>
    cva('relative z-2 w-full rounded-t border-b bg-transparent px-4', {
      variants: {
        size: {
          default: 'h-18',
          compact: 'h-12'
        }
      }
    })({
      size: props.sizeVariant
    })
  )

  const searchOverlayLabelBase = computed(() =>
    cva('text-bt-gray pointer-events-none absolute left-0 top-0 z-1 flex select-none items-center pl-4 text-gray-400', {
      variants: {
        size: {
          default: 'h-18',
          compact: 'h-12'
        }
      }
    })({
      size: props.sizeVariant
    })
  )
</script>

<template>
  <div
    class="w-full bg-white lg:max-w-none"
    :class="{ relative: isDefaultVariant }"
  >
    <LazyOrigDestInputBlock
      v-if="variant === 'block'"
      :label="label"
      :title="title"
      :selected="modelValue"
      :class="blockBorderBase"
      :type="type"
      @click="openModal()"
      @keydown.prevent.enter="openModal(true)"
    />

    <ClientOnly v-if="isDefaultVariant">
      <template #fallback>
        <UiModalTrigger
          :label="label"
          :title="$t(`origDest.${type}.select`)"
          :icon="icon"
          :aria-label="triggerAriaLabel"
        />
      </template>

      <LazyUiModalTrigger
        :label="label"
        :title="title"
        :icon="icon"
        :size="sizeVariant"
        :is-modal-open="isOpen"
        :aria-label="triggerAriaLabel"
        @click="openModal()"
        @keydown.prevent.enter="openModal(true)"
      />
    </ClientOnly>

    <LazyUiModalInput
      v-model="isOpen"
      :title="$t('origDest.title')"
      :show-close="!isDesktop"
      :has-offset="isDesktop && isDefaultVariant && !openOverTrigger"
      :dialog-content-class="dialogContentClass"
      @update:model-value="searchTerm = ''"
    >
      <template #content>
        <div
          ref="origDestSelect"
          class="bg-white"
        >
          <div class="relative mx-0.5 mt-0.5">
            <label
              v-if="searchTerm.length"
              :class="searchOverlayLabelBase"
              v-html="autoCompleteSuggestion"
            />
            <input
              ref="searchInput"
              v-model="searchTerm"
              type="text"
              :class="searchInputBase"
              :placeholder="$t('origDest.search.placeholder')"
              @keydown.prevent.enter="onSuggestionSelect"
              @update:model-value="isSubCategoriesDisabled = true"
            />
          </div>
          <LazyOrigDestAnyDestination
            v-if="showAnyDestinOption && type === DESTIN && selectedOrigin?.isBtOrig && !!anyDestinationLink?.href"
            :any-dest-link="anyDestinationLink?.href"
            :selected-origin-code="selectedOrigin.code"
          />
          <OrigDestCategories
            v-if="isDesktop && !searchTerm && categories"
            v-model="categoryModel"
            class="flex h-auto w-full items-center gap-2 border-b p-4"
            :categories="categories"
            @update:model-value="isSubCategoriesDisabled = isKeyboardNavigated"
            @keydown.tab="isSubCategoriesDisabled = false"
          />
          <div class="flex h-auto pb-2 md:h-[40vh] md:max-h-90 md:pb-0">
            <OrigDestSubCategories
              v-if="isWideSelect"
              v-model="subCategoryModel"
              class="min-w-68 overflow-auto"
              :items="subCategories"
              :name="$t(`origDest.subCategories.${categoryModel}`)"
              :disabled="isSubCategoriesDisabled"
              @mouseover="isSubCategoriesDisabled = false"
            />
            <OrigDestList
              v-model="modelValue"
              v-model:isSubCategoriesDisabled="isSubCategoriesDisabled"
              v-model:is-open="isOpen"
              v-model:search-term="searchTerm"
              :data="data"
              :category="categoryModel"
              :sub-category="subCategoryModel"
              @selected="emit('selected')"
              @keydown.exact.prevent.tab="searchInput?.focus()"
            />
          </div>
        </div>
      </template>
    </LazyUiModalInput>
  </div>
</template>
